/**
 * What the maintenance takeover says about time. The API's 503 carries
 * `until` (ISO 8601, set by the operator next to MAINTENANCE_MODE) and
 * an optional `reason` copy key; these helpers turn them into the
 * screen's lines. Pure, so the unit tests can pin the clock.
 */

export type MaintenanceEta =
  | { kind: "none" }
  | { kind: "minutes"; minutes: number; until: Date }
  | { kind: "overdue"; until: Date };

/** Minutes left rounded to the nearest (it reads "about N"), never
 *  below 1 while the end is ahead; "overdue" once it has passed, so the
 *  screen never counts below zero. */
export function maintenanceEta(until: string | null | undefined, now: number): MaintenanceEta {
  if (!until) return { kind: "none" };
  const at = Date.parse(until);
  if (!Number.isFinite(at)) return { kind: "none" };
  const left = at - now;
  if (left <= 0) return { kind: "overdue", until: new Date(at) };
  return { kind: "minutes", minutes: Math.max(1, Math.round(left / 60_000)), until: new Date(at) };
}

/** Reasons with their own sentence on the screen. */
export const MAINTENANCE_REASONS = ["server_upgrade"] as const;
export type MaintenanceReason = (typeof MAINTENANCE_REASONS)[number];

/** The 503 body's fields the takeover uses; anything else is ignored. */
export function readMaintenanceBody(body: unknown): { until: string | null; reason: MaintenanceReason | null } {
  const b = body && typeof body === "object" ? (body as Record<string, unknown>) : {};
  const reason = typeof b.reason === "string" && (MAINTENANCE_REASONS as readonly string[]).includes(b.reason)
    ? (b.reason as MaintenanceReason)
    : null;
  return { until: typeof b.until === "string" ? b.until : null, reason };
}

/** A fetch the caller cancelled (navigation, a newer search): never a
 *  sign that the API is down. */
export function isAbortError(err: unknown): boolean {
  return !!err && typeof err === "object" && (err as { name?: unknown }).name === "AbortError";
}

/** Where a tab keeps the last announced end, so a reload during a
 *  power-off (when the API cannot say it) still shows the time. */
export const MAINTENANCE_STORE_KEY = "menzil_maintenance";
const STORE_MAX_AGE_MS = 3 * 60 * 60_000;

/** The stored announcement if it is recent enough to still describe
 *  this window; null when absent, stale or unreadable. */
export function savedAnnouncement(
  raw: string | null,
  now: number,
): { until: string | null; reason: MaintenanceReason | null } | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as unknown;
    if (!v || typeof v !== "object") return null;
    const at = (v as { at?: unknown }).at;
    if (typeof at !== "number" || now - at > STORE_MAX_AGE_MS || at > now + 60_000) return null;
    const m = readMaintenanceBody(v);
    return m.until || m.reason ? m : null;
  } catch {
    return null;
  }
}
